/* Чат на главной. Помощник не «придумывает» ответы: госпошлину считает сервер по НК РФ, документы собираются
   по образцам адвоката. Сценарий — асинхронная функция: ask() показывает вопрос и ждёт ответа человека. */
import { Fragment, useContext, useState, type ReactNode } from "react";
import { ApiError, downloadDocument, getJson, postJson } from "../api";
import { boot } from "../boot";
import { openPreview } from "../components/Dialogs";
import { chooseDocument } from "../components/DocPicker";
import { DocPage } from "../components/DocPage";
import { Icon } from "../components/Icon";
import { openViewer } from "../components/Viewer";
import { pickFile, READING } from "../lib/files";
import { looksLikeDocument, textFile } from "../lib/pasted";
import { absorbPrefill, remaining, type DocState, type Prefill, type Schema, type Source } from "../lib/prefill";
import { checkField, choiceLabel, MONEY_RE, nb, plural, relevant, type Field } from "../lib/text";
import {
  addSaved, cachedChat, calcOpener, chatsStore, currentChatStore, docName, foldersStore, loadChat, promptLogin, rememberChat,
  savedStore, Store, uploadedStore, uploadFile, upsertChat, useStore, whenSavedReady, whenUploadedReady, type ChatBrief, type
  Folder, type SavedItem, type UploadedDoc,
} from "../store";
import { Checklist } from "./checklist";
import {
  DEFAULT_HINT, DEFAULT_PLACEHOLDER, composerStore, messagesStore, QuestionClosed, resetChatState, reviewStore, type Choice,
  type ComposerState,
} from "./state";
import { ArticleText, articleId } from "./article";
import { PlenumText, isExplanation, plenumChecked } from "./plenum";
import { DecisionText, isDecision } from "./practice";
import { CourtDecisionText, isCourtDecision } from "./courts";
import { AnswerPoints, SummaryCard, type ReviewAnswer } from "./review";
import { createWorkspace } from "./workspace";

class Cancelled extends Error {}

const COURTS: Choice[] = [
  { label: "Суд общей юрисдикции или мировой судья", value: "general" },
  { label: "Арбитражный суд", value: "arbitrazh" },
];
const PAYERS: Choice[] = [{ label: "Гражданин", value: "person" }, { label: "Организация", value: "org" }];
const MULTILINE = new Set(["lines", "block"]);   // ответ в несколько строк: Enter — новая строка
const MESSAGE_SOURCE = "ваше сообщение";        // так сервер подписывает данные, взятые помощником из сообщения
// Временные сообщения («Думаю…», «Читаю документ…») в сохранённый чат не попадают.
const RESUME = "Это сохранённый чат. Продолжите разговор — напишите, что нужно сделать.";
const SEARCHING = "Ищу в документе данные для этого шаблона — обычно 5–20 секунд…";
const FORMULATING = "Формулирую юридическим языком — несколько секунд…";
// Ответ по праву (DeepSeek V4.1 Flash сначала рассуждает) — обычно 20–30 секунд: дольше 8 секунд — объясняем, что идёт.
const SLOW_ANSWER = "Ищу статьи в базе и сверяю цитаты с законом — обычно до полуминуты…";
const SLOW_AFTER_MS = 8000;
const LAW_EXAMPLES = ["Можно ли уволить беременную сотрудницу?", "Какой срок исковой давности по долгу?", "Кто расследует кражу?"];
const BACK = "\u0000назад";                        // ответ «Назад»: вернуться к предыдущему вопросу мастера
const LEGAL_PROMPT = "Так это будет в документе:";
const TRANSIENT = new Set([READING, RESUME, "Думаю…", SEARCHING, FORMULATING, SLOW_ANSWER]);

type DutyKind = { kind: string; title: string; needs_price: boolean; needs_payer: boolean };
type Calc = { title: string; what: string; to_pay: string; amount: string; rounded: boolean; formula: string; norm: string; edition: string };
// Вид документа определяет выжимку (судебный акт, жалоба, иск, договор…) и кнопки под ней (pravosvod/review.py).
type ReviewProfile = { key: string; title: string; summary: string; issues: string; questions: { label: string; question: string }[] };
type ReviewState = {
  document: { id: string; filename: string; template: string | null };
  profile: ReviewProfile;
  status: "idle" | "running" | "ready" | "error";
  messages: { question: string; answer: ReviewAnswer; mode?: string }[];
  pending_question: string;
  pending_mode: string;
  error: string;
};
type InterviewStep = { values: Record<string, { value: string; quote: string; direct?: boolean }>; question: string; keys: string[] };
type Formulated = { text: string; missing: string[] };
// Госпошлина за документ — калькулятором на сервере (constructor.duty_info): сумма с расчётом или сверенная льгота.
type DutyInfo = {
  exempt?: string; checked?: string; title?: string; price?: string; to_pay?: string; amount?: string; rounded?: boolean;
  formula?: string; norm?: string; edition?: string; note?: string;
};
type AssistantReply = {
  action: string; text: string; points?: { text: string; quote: string; source: string; checked?: string; template?: string; source_id?: string }[];
  answer?: string; suggestions?: string[];   // ответ по праву: краткий вывод по цитатам и похожие вопросы
  articles?: { source_id: string; label: string }[];   // подтверждённого ответа нет — статьи, где он может быть
  template?: string; prefill?: Prefill; duty?: Calc | null; suggest?: { slug: string; title: string };
};

export { QuestionClosed };

const p = (text: ReactNode, className?: string) => <p className={className}>{text}</p>;

function flatten(children: ReactNode[]): ReactNode[] {
  const out: ReactNode[] = [];
  for (const child of children) {
    if (Array.isArray(child)) out.push(...flatten(child));
    else if (child !== null && child !== undefined && child !== false) out.push(child);
  }
  return out.map((child, i) => <Fragment key={i}>{child}</Fragment>);
}

const chip = (label: string, onClick: () => void) => <button type="button" className="chip" onClick={onClick}>{label}</button>;

// Кнопки разбора недоступны, пока идёт разбор, и после того, как разбор закрыт.
function ReviewButton({ epoch, className, onClick, children }: { epoch: number; className: string; onClick: () => void; children: ReactNode }) {
  const review = useStore(reviewStore);
  return <button type="button" className={className} disabled={review.busy || review.epoch !== epoch} onClick={onClick}>{children}</button>;
}

function BusyButton({ className, onClick, children }: { className: string; onClick: () => void; children: ReactNode }) {
  const { busy } = useStore(reviewStore);
  return <button type="button" className={className} disabled={busy} onClick={onClick}>{children}</button>;
}

// «Прикрепить документ» во вступлении к шаблону: окно выбора файла — сразу по нажатию, загрузка — после выбора.
// «Прикрепить документ» во вступлении к шаблону: файл из «Моих документов», целая папка или новый с компьютера.
function AttachChip({ label, run }: { label: string; run: () => Promise<void> }) {
  const closed = useContext(QuestionClosed);
  const [busy, setBusy] = useState(false);
  return (
    <button type="button" className="attach-button" disabled={closed || busy} onClick={async () => {
      setBusy(true);
      try { await run(); } finally { setBusy(false); }
    }}><Icon name="clip" />{label}</button>
  );
}

// Откуда взять документ: новый файл с компьютера, загруженный раньше или целая папка «Моих документов».
type DocSource = { type: "file"; file: File } | { type: "doc"; doc: UploadedDoc } | { type: "folder"; folder: Folder; docs: UploadedDoc[] };

async function chooseSource(title: string, folders: boolean, usable?: (doc: UploadedDoc) => boolean): Promise<DocSource | null> {
  await whenUploadedReady();
  // Своих файлов ещё нет — сразу окно выбора файла, без лишнего шага.
  const picked = uploadedStore.get().some((doc) => !usable || usable(doc)) ? await chooseDocument({ title, folders, usable }) : { type: "upload" as const };
  if (!picked) return null;
  if (picked.type === "upload") {
    const file = await pickFile();
    return file ? { type: "file", file } : null;
  }
  return picked;
}

// «Назад» под вопросом мастера: вернуться к предыдущему вопросу и дополнить ответ.
function BackChip({ onClick }: { onClick: () => void }) {
  const closed = useContext(QuestionClosed);
  return (
    <button type="button" className="chip chip-quiet chip-back" aria-label="Назад к предыдущему вопросу" disabled={closed}
      onClick={onClick}><Icon name="arrow" />Назад</button>
  );
}

// Расчёт госпошлины из сводки — в «Мои документы», как расчёт калькулятора.
function SaveCalcButton({ calc }: { calc: Calc }) {
  const [state, setState] = useState<"idle" | "busy" | "done">("idle");
  return (
    <button type="button" className="chip" disabled={state !== "idle"} onClick={async () => {
      setState("busy");
      try { await addSaved("calc", calc.title, calc.to_pay, calc); setState("done"); } catch { setState("idle"); }
    }}>{state === "done" ? "Расчёт сохранён в «Моих документах»" : "Сохранить расчёт"}</button>
  );
}

function DownloadButton({ busy, disabled, onClick }: { busy: Store<boolean>; disabled: boolean; onClick: () => void }) {
  const working = useStore(busy);
  return <button type="button" className="button" disabled={disabled || working} onClick={onClick}>Скачать Word</button>;
}

export type Chat = ReturnType<typeof createChat>;

export function createChat(env: { input: () => HTMLTextAreaElement | null; messages: () => HTMLElement | null }) {
  const workspace = createWorkspace();
  const templates = boot.templates;
  const aiEnabled = boot.aiEnabled;
  let nextId = 1;
  let pending: {
    resolve: (value: string) => void; reject: (error: Error) => void; choices: Choice[]; skip: string | null;
    id: number; multiline: boolean; field: Field | null; attachable: boolean; back: boolean;
  } | null = null;
  let flow = 0;           // номер текущего сценария; старый сценарий, увидев новый номер, останавливается
  let kinds: Record<string, DutyKind[]> | null = null;
  let reviewDoc: string | null = null;
  let reviewIntro = false;
  let reviewBusy = false;
  let reviewTimer: ReturnType<typeof setTimeout> | undefined;
  let reviewStamp = "";
  let reviewEpoch = 0;
  let docFlow: DocState | null = null;
  let queued: string | null = null;      // ответ, написанный до того, как мастер задал вопрос
  const assistantHistory: { role: string; text: string }[] = [];
  // Чат закрыт переходом в другой раздел (dispose): запоздавшие ответы сервера на экран и в адрес не попадают.
  let alive = true;

  const input = () => env.input()!;
  const composer = (change: Partial<ComposerState>) => {
    if (alive) composerStore.update((state) => ({ ...state, ...change }));
  };
  const flowBar = (change: Partial<{ hidden: boolean; title: string; cancel: string }>) => {
    if (alive) composerStore.update((state) => ({ ...state, flow: { ...state.flow, ...change } }));
  };

  /* ---- сообщения */

  function startChatting() {
    if (!alive || document.body.classList.contains("chatting")) return;
    document.body.classList.add("chatting");
    workspace.refresh();
  }

  function say(...children: ReactNode[]): number {
    return sayWith(null, ...children);
  }

  // footer — кнопки под облачком сообщения («Прикрепить документ»).
  function sayWith(footer: ReactNode, ...children: ReactNode[]): number {
    if (!alive) return -1;
    startChatting();
    const id = nextId++;
    messagesStore.update((list) => [...list, { id, role: "bot", parts: flatten(children), footer: footer || undefined }]);
    return id;
  }

  function said(text: string) {
    if (!alive) return;
    startChatting();
    const id = nextId++;
    messagesStore.update((list) => [...list, { id, role: "user", text }]);
  }

  const remove = (id: number) => { if (alive) messagesStore.update((list) => list.filter((m) => m.id !== id)); };
  // Новый текст в том же облачке: «Думаю…» → «Ищу статьи…» — одно сообщение, а не второе под первым.
  const reword = (id: number, ...children: ReactNode[]) => {
    if (alive) messagesStore.update((list) => list.map((m) => (m.id === id ? { ...m, parts: flatten(children) } : m)));
  };
  const clear = () => { if (alive) messagesStore.set([]); };
  const warn = (text: string) => say(p(text, "msg-error"));

  // Три главных действия; все шаблоны — на отдельной странице, а не стеной кнопок в ответе.
  function menu() {
    return (
      <div className="chips">
        {aiEnabled && chip("Вопрос по праву", () => { said("Вопрос по праву"); lawWelcome(); })}
        {chip("Разобрать документ", reviewWelcome)}
        {chip("Посчитать госпошлину", () => { said("Посчитать госпошлину"); startDuty(); })}
        <a className="chip" href="/documents">Шаблоны документов</a>
      </div>
    );
  }

  // Консультация по праву: помощник отвечает со ссылками на статьи (pravosvod/assistant.py); примеры — сразу вопросом.
  function lawWelcome() {
    say(p("Задайте вопрос по праву — отвечу со ссылками на статьи кодексов и законов в действующей редакции с pravo.gov.ru. "
      + "Каждое утверждение — с цитатой из статьи."),
      p("Например:", "msg-hint"),
      <div className="chips">{LAW_EXAMPLES.map((question) => (
        <Fragment key={question}>{chip(question, () => { said(question); askAssistant(question); })}</Fragment>
      ))}</div>);
    composer({ placeholder: "Ваш вопрос по праву" });
    input().focus();
  }

  /* ---- вопрос — ответ */

  type Ask = {
    text?: string; hint?: string; progress?: string; choices?: Choice[]; skip?: string | null; placeholder?: string;
    body?: ReactNode[]; extra?: ReactNode[]; multiline?: boolean; field?: Field | null; attachable?: boolean; back?: boolean;
  };

  // body — что показать перед вопросом (вступление, образец); extra — кнопки рядом с вариантами.
  // field — вопрос шаблона (его может заполнить прикреплённый файл); attachable — файл и есть ответ.
  // back — кнопка «Назад» (и слово «назад»): ответ BACK, мастер вернётся к предыдущему вопросу.
  function ask({ text, hint, progress, choices = [], skip = null, placeholder, body = [], extra = [], multiline = false, field = null, attachable = false, back = false }: Ask) {
    if (!alive) return Promise.reject(new Cancelled());
    const id = say(progress ? p(progress, "msg-progress") : null, body, text ? p(text, "msg-question") : null, hint ? p(hint, "msg-hint") : null);
    const buttons = back ? [...extra, <BackChip onClick={() => answer(BACK, "Назад")} />] : extra;
    messagesStore.update((list) => list.map((m) => (m.id === id && m.role === "bot"
      ? { ...m, question: { choices, skip, extra: buttons, closed: false, choose: answer } } : m)));
    composer({ placeholder: placeholder || (choices.length ? "Выберите вариант выше или напишите" : "Ваш ответ"),
      hint: multiline ? "Enter — новая строка · Ctrl+Enter — отправить" : DEFAULT_HINT });
    if (!choices.length) input().focus({ preventScroll: true });
    const answered = new Promise<string>((resolve, reject) => { pending = { resolve, reject, choices, skip, id, multiline, field, attachable, back }; });
    if (queued !== null) {                       // человек ответил, пока вопрос готовился
      const text = queued;
      queued = null;
      answerQuietly(text);
    }
    return answered;
  }

  function closePending(chosen?: string) {
    const current = pending!;
    pending = null;
    composer({ placeholder: DEFAULT_PLACEHOLDER, hint: DEFAULT_HINT });
    if (alive) messagesStore.update((list) => list.map((m) => (m.id === current.id && m.role === "bot" && m.question
      ? { ...m, question: { ...m.question, closed: true, chosen } } : m)));
    return current;
  }

  function answer(value: string, label: string) {
    if (!pending) return;
    const { resolve } = closePending(label);
    said(label);
    resolve(value);
  }

  // Ответ без реплики пользователя: значение взято из прикреплённого документа.
  function answerQuietly(value: string) {
    if (pending) closePending().resolve(value);
  }

  function cancelPending() {
    queued = null;
    if (pending) closePending().reject(new Cancelled());
  }

  function runFlow(title: string, steps: (guard: () => void) => Promise<void>) {
    leaveReview();
    flowBar({ cancel: "Прервать" });
    const id = ++flow;
    cancelPending();
    flowBar({ title, hidden: false });
    const guard = () => { if (id !== flow) throw new Cancelled(); };
    steps(guard)
      .catch((error) => { if (!(error instanceof Cancelled)) warn((error as Error).message); })
      .finally(() => { if (id === flow) flowBar({ hidden: true }); });
  }

  function cancelFlow() {
    leaveReview();
    flow += 1;
    cancelPending();
    flowBar({ hidden: true });
    workspace.show(null, "Рабочий стол", "home");
    say(p("Остановились. Чем ещё помочь?"), menu());
  }

  /* ---- разбор загруженного документа */

  function setReviewBusy(value: boolean) {
    reviewBusy = value;
    if (alive) reviewStore.set({ busy: value, epoch: reviewEpoch });
  }

  function leaveReview() {
    reviewEpoch += 1;
    clearTimeout(reviewTimer);
    reviewDoc = null;
    reviewIntro = false;
    reviewStamp = "";
    setReviewBusy(false);
    composer({ placeholder: DEFAULT_PLACEHOLDER });
  }

  function reviewWelcome() {
    leaveReview();
    detachChat();                                    // экран очищается — сохранённый чат не трогаем
    ++flow;
    cancelPending();
    reviewIntro = true;
    clear();
    flowBar({ hidden: false, title: "Разбор документа", cancel: "Закрыть разбор" });
    workspace.show("review", "Загрузить документ", "review");
    sayWith(<BusyButton className="attach-button" onClick={attachForReview}><Icon name="clip" />Прикрепить документ</BusyButton>,
      p("Разберём ваш документ", "msg-question"),
      p("Прикрепите судебный акт, жалобу, иск, договор, заявление, запрос или другой документ — или вставьте его текст в поле ввода. Подготовлю выжимку по виду документа, помогу найти нужное место и объяснить его смысл."),
      <ul className="review-intro-list">
        <li>Судебный акт — суд, дата, номер дела, стороны, доводы сторон, выводы суда и решение</li>
        <li>Жалоба и иск — доводы, нормы, на которые ссылается сторона, и что она просит</li>
        <li>Договор — суть, оплата, сроки, обязанности сторон и что они значат на практике</li>
        <li>Заявление о преступлении — что произошло, ущерб, статья, которую называет заявитель</li>
        <li>Список норм и практики из текста, спорные и слабые места — с пояснением</li>
        <li>Каждый пункт — с цитатой из вашего файла</li>
      </ul>,
      p(aiEnabled ? "Подойдут PDF, Word (.docx), TXT, скан или фото JPEG/PNG. До 10 МБ." : "Подойдут PDF с текстом, Word (.docx) и TXT. Разбор с ИИ сейчас недоступен; файл можно сохранить.", "msg-hint"),
      p("Документ и переписка останутся в «Моих документах».", "review-footnote"));
    composer({ placeholder: "Прикрепите документ или вставьте его текст…" });
  }

  // Выжимка — карточкой по разделам вида документа; спорные места — с пояснениями; ответ на вопрос — пунктами.
  function reviewAnswer(result: ReviewAnswer, docId: string, title: string) {
    const body = result.profile ? <SummaryCard answer={result} docId={docId} /> : <AnswerPoints answer={result} docId={docId} />;
    return say(p(title, "msg-question"), body,
      result.clarification ? p(result.clarification, "review-clarification") : null,
      result.note ? p(result.note, "note") : null);
  }

  function reviewTools(state: ReviewState) {
    const { document: doc, profile } = state;
    const fill = (text: string) => { input().value = text; autoGrow(); input().focus(); };
    const epoch = reviewEpoch;
    return (
      <div className="chips review-tools">
        <ReviewButton epoch={epoch} className="chip" onClick={() => requestReview("", "issues")}>{profile.issues}</ReviewButton>
        {profile.questions.map((item) => (
          <ReviewButton key={item.label} epoch={epoch} className="chip" onClick={() => requestReview(item.question)}>{item.label}</ReviewButton>
        ))}
        <ReviewButton epoch={epoch} className="chip" onClick={() => fill("Объясни пункт ")}>Объяснить пункт</ReviewButton>
        <ReviewButton epoch={epoch} className="chip" onClick={() => fill("Предложи новую редакцию пункта … в интересах …")}>Предложить правки</ReviewButton>
        {doc.template && <a className="chip" href={`/?start=doc:${doc.template}&from=${doc.id}`}>Составить возражения</a>}
      </div>
    );
  }

  function renderReview(state: ReviewState) {
    const stamp = JSON.stringify([state.status, state.messages.length, state.pending_question, state.pending_mode, state.error]);
    setReviewBusy(state.status === "running");
    if (stamp === reviewStamp) return;
    reviewStamp = stamp;
    clear();
    const doc = state.document;
    say(<div className="review-document">
      <div>{p("РАЗБОР ДОКУМЕНТА", "review-eyebrow")}{p(doc.filename, "msg-question")}</div>
      <button type="button" className="chip chip-quiet" onClick={() => openViewer({ kind: "upload", id: doc.id })}>Открыть документ</button>
    </div>,
    p("Цитаты сверены с извлечённым текстом. Выводы и правки предлагает ИИ; проверка по действующему законодательству в этот разбор не входит.", "review-footnote"));
    state.messages.forEach((turn, index) => {
      if (turn.question) said(turn.question);
      const title = turn.answer.profile ? state.profile.summary : turn.mode === "issues" ? turn.question
        : index === 0 && !turn.question ? "Краткий разбор" : "Ответ по документу";
      reviewAnswer(turn.answer, doc.id, title);
    });
    if (state.pending_question) said(state.pending_question);
    if (state.status === "running") {
      say(<div className="review-wait" role="status"><span className="review-pulse" aria-hidden="true" />
        {p(state.messages.length ? "Ищу ответ и проверяю цитаты…" : "Читаю документ и собираю выжимку…")}</div>,
      p("Для длинного документа это может занять несколько минут. Можно закрыть страницу и вернуться через «Мои документы».", "review-footnote"));
    } else if (state.status === "error") {
      say(p(state.error, "msg-error"), <div className="chips">{chip("Повторить", () => requestReview(state.pending_question, state.pending_mode))}</div>);
    } else if (state.status === "ready") {
      say(p("Что хотите уточнить?", "msg-hint"), reviewTools(state));
    }
    setReviewBusy(state.status === "running");
  }

  function pollReview(id: string, epoch: number, failures = 0) {
    clearTimeout(reviewTimer);
    reviewTimer = setTimeout(async () => {
      try {
        const state = await getJson<ReviewState>(`/api/documents/${id}/review`);
        if (epoch !== reviewEpoch || id !== reviewDoc) return;
        renderReview(state);
        if (state.status === "running") pollReview(id, epoch);
      } catch {
        if (epoch !== reviewEpoch) return;
        if (failures < 3) pollReview(id, epoch, failures + 1);
        else {
          setReviewBusy(false);
          say(p("Не удалось обновить разбор. Работа на сервере может продолжаться.", "msg-error"),
            <div className="chips">{chip("Обновить разбор", () => openReview(id))}</div>);
        }
      }
    }, 2500);
  }

  // mode: "issues" — спорные места с пояснениями (вопрос подставит сервер по виду документа).
  async function requestReview(question = "", mode = "") {
    if (!reviewDoc || reviewBusy) return;
    const id = reviewDoc;
    const epoch = reviewEpoch;
    setReviewBusy(true);
    try {
      const state = await postJson<ReviewState>(`/api/documents/${id}/review`, { question, mode });
      if (epoch !== reviewEpoch) return;
      renderReview(state);
      if (state.status === "running") pollReview(id, epoch);
    } catch (error) {
      if (epoch !== reviewEpoch) return;
      setReviewBusy(false);
      say(p((error as Error).message, "msg-error"), <div className="chips">{chip("Повторить", () => requestReview(question, mode))}</div>);
      if (question) { input().value = question; autoGrow(); }
    }
  }

  async function openReview(id: string) {
    leaveReview();
    detachChat();
    ++flow;
    cancelPending();
    reviewDoc = id;
    const epoch = reviewEpoch;
    clear();
    flowBar({ hidden: false, title: "Разбор документа", cancel: "Закрыть разбор" });
    composer({ placeholder: "Задайте вопрос по документу…" });
    workspace.show(`review:${id}`, "Разбор документа", "review", null, new URLSearchParams(location.search).get("start") === "review");
    say(p("Открываю документ…", "msg-hint"));
    setReviewBusy(true);
    try {
      const state = await getJson<ReviewState>(`/api/documents/${id}/review`);
      if (epoch !== reviewEpoch) return;
      renderReview(state);
      if (state.status === "idle") await requestReview();
      else if (state.status === "running") pollReview(id, epoch);
    } catch (error) {
      if (epoch !== reviewEpoch) return;
      setReviewBusy(false);
      warn((error as Error).message);
    }
  }

  async function attachForReview() {
    if (reviewBusy) return;
    const source = await chooseSource("Какой документ разобрать?", false);
    if (!source) return;
    if (source.type !== "file") {
      if (source.type === "doc") await openReview(source.doc.id);
      return;
    }
    await reviewFile(source.file, `Документ: ${source.file.name}`);
  }

  // Текст документа, вставленный в поле ввода: разбираем как файл .txt — он останется в «Моих документах».
  function reviewText(text: string) {
    detachChat();                                    // экран очищается — сохранённый чат не трогаем
    clear();
    flowBar({ hidden: false, title: "Разбор документа", cancel: "Закрыть разбор" });
    const file = textFile(text);
    return reviewFile(file, `Текст документа: ${file.name.replace(/\.txt$/, "")}`);
  }

  async function reviewFile(file: File, label: string) {
    leaveReview();
    ++flow;
    cancelPending();
    reviewIntro = true;
    workspace.show("review", "Загрузить документ", "review");
    const epoch = ++reviewEpoch;
    setReviewBusy(true);
    said(label);
    const waiting = say(p(READING, "msg-hint"));
    try {
      const doc = await uploadFile(file);
      if (epoch !== reviewEpoch) return;
      remove(waiting);
      await openReview(doc.id);
    } catch (error) {
      if (epoch !== reviewEpoch) return;
      remove(waiting);
      warn((error as Error).message);
      setReviewBusy(false);
    }
  }

  /* ---- госпошлина */

  function startDuty() {
    workspace.show("duty", "Рассчитать госпошлину", "calc");
    runFlow("Расчёт госпошлины", async (guard) => {
      kinds = kinds || await getJson<Record<string, DutyKind[]>>("/api/duty/kinds");
      guard();
      const court = await ask({ text: "Посчитаем госпошлину. Куда подаёте?", choices: COURTS });
      const kindValue = await ask({ text: "Что подаёте?", choices: kinds[court].map((k) => ({ label: k.title, value: k.kind })) });
      const kind = kinds[court].find((k) => k.kind === kindValue)!;
      const payer = kind.needs_payer ? await ask({ text: "Кто подаёт?", choices: PAYERS }) : "person";
      let price: string | null = null;
      let data: Omit<Calc, "title" | "what">;
      for (;;) {
        if (kind.needs_price) {
          price = (await ask({ text: "Какая цена иска, в рублях?", hint: "Например: 150 000 или 386 781,63", placeholder: "Цена иска" })).trim();
          if (!MONEY_RE.test(price)) { warn("Нужна сумма цифрами, например 150 000 или 386 781,63."); continue; }
        }
        try {
          data = await postJson("/api/duty", { kind: kind.kind, court, price, payer });
          break;
        } catch (error) {
          guard();
          if (!kind.needs_price) throw error;
          warn((error as Error).message);
        }
      }
      guard();
      const what = [kind.title, court === "arbitrazh" ? "арбитражный суд" : "суд общей юрисдикции",
        kind.needs_payer ? (payer === "org" ? "организация" : "гражданин") : "",
        price ? `цена иска ${price} ₽` : ""].filter(Boolean).join(", ");
      const calc: Calc = { title: kind.title, what, ...data };
      showCalc(calc);
      addSaved("calc", kind.title, data.to_pay, calc).catch(() => { /* расчёт уже показан; не сохранился — не страшно */ });
    });
  }

  function showCalc(item: Calc, savedId: string | null = null) {
    if (savedId) {
      leaveReview();
      ++flow;
      cancelPending();
      flowBar({ hidden: true });
      workspace.show(`calc:${savedId}`, "Расчёт госпошлины", "calc");
    }
    say(
      p("Госпошлина к оплате", "result-label"),
      p(nb(item.to_pay), "result-amount"),
      item.rounded ? p(nb(`Точно по формуле ${item.amount}; к оплате округлено вверх до рубля, чтобы иск не оставили без движения из-за недоплаты.`), "result-note") : null,
      <dl className="facts">
        <dt>Что подаёте</dt><dd>{nb(item.what)}</dd>
        <dt>Расчёт</dt><dd>{nb(item.formula)}</dd>
        <dt>Норма</dt><dd>{`${item.norm} (${item.edition})`}</dd>
      </dl>,
      p("Льготы по ст. 333.36 и 333.37 НК РФ калькулятор пока не учитывает.", "msg-hint"),
      <div className="chips">
        {chip("Посчитать ещё", () => { said("Посчитать ещё"); startDuty(); })}
        <a className="chip" href="/documents">Составить документ</a>
      </div>);
  }

  /* ---- документы */

  function skipLabel(field: Field): string | null {
    if (!field.optional) return null;
    if (!field.default) return "Пропустить";
    return field.default.includes("{today}") ? "Пропустить — поставлю сегодняшнюю дату" : "Пропустить — оставлю текст из образца";
  }

  // Приложения — флажками (и своё); ничего не отмечено — «Без приложений», само ничего не подставляется.
  async function askChecklist(field: Field, progress: string, current = "", back = false): Promise<string> {
    const initial = current.split("\n").map((line) => line.trim()).filter(Boolean);
    const value = await ask({
      text: field.question, progress, field, back,
      hint: "Отметьте то, что у вас есть, и добавьте своё. Само ничего не подставлю. Можно и написать — через точку с запятой.",
      extra: [<Checklist options={field.options || []} initial={initial}
        onDone={(items) => answer(items.join("\n"), items.length ? `Приложения: ${items.join("; ")}` : "Без приложений")} />],
      placeholder: "Или напишите приложения через точку с запятой",
    });
    if (value === BACK) return BACK;
    return value.split(/\n|;/).map((line) => line.trim()).filter(Boolean).join("\n");
  }

  // draft — текст, который человек дописывает («Дополнить рассказ», «Назад»): он уже стоит в поле ввода.
  // back — кнопка «Назад»: тогда ответ может быть BACK.
  async function askField(field: Field, progress: string, current = "", draft = "", back = false): Promise<string> {
    if (field.kind === "checklist") return askChecklist(field, progress, current, back);
    const multiline = MULTILINE.has(field.kind);
    const example = multiline ? field.example.split("\n")[0] : field.example;
    const hints = [multiline ? "Каждый пункт — с новой строки (Enter). Отправить — кнопкой со стрелкой или Ctrl+Enter." : "",
      !field.choices.length && example ? `Например: ${example}` : ""];
    for (;;) {
      const asking = ask({
        text: field.question,
        hint: hints.filter(Boolean).join(" "),
        progress,
        multiline,
        field,
        back,
        choices: field.choices.map((c) => ({ label: choiceLabel(c), value: c })),
        skip: skipLabel(field),
        placeholder: field.kind === "date" ? "ДД.ММ.ГГГГ" : field.kind === "money" ? "Сумма цифрами"
          : multiline ? "Каждый пункт с новой строки" : "Ваш ответ",
      });
      if (draft) { input().value = draft; autoGrow(); draft = ""; }
      const value = (await asking).trim();
      if (value === BACK) return BACK;
      const error = checkField(field, value);
      if (!error) return value;
      warn(error);
    }
  }

  // Рассказ и доводы — юридическим языком (pravosvod/formulate.py): сервер проверил, что даты, суммы, имена — из ответов
  // человека, а норм и новых фактов не добавлено. Человек выбирает: эта редакция, своя или дописать рассказ.
  async function polish(state: DocState, field: Field, value: string, guard: () => void): Promise<string> {
    if (!aiEnabled || !field.legal || value.trim().length < 15) return value;
    const waiting = say(p(FORMULATING, "msg-hint"));
    let result: Formulated;
    try {
      result = await postJson<Formulated>(`/api/templates/${state.schema.slug}/formulate`,
        { key: field.key, text: value, answers: state.answers });
    } catch (error) {
      guard();
      if (error instanceof ApiError && error.status === 422) say(p(error.message, "msg-hint"));
      return value;                              // не вышло — остаётся текст человека
    } finally {
      remove(waiting);
    }
    guard();
    const choice = await ask({
      body: [p(LEGAL_PROMPT, "msg-question"),
        <div className="legal-text">{result.text.split("\n").map((line, i) => <p key={i}>{line}</p>)}</div>,
        result.missing.length ? (
          <div className="legal-missing">
            {p("Чтобы текст был убедительнее, можно добавить:")}
            <ul>{result.missing.map((item, i) => <li key={i}>{item}</li>)}</ul>
          </div>
        ) : null,
        p("Даты, суммы и имена — только из вашего ответа; новых фактов и ссылок на нормы не добавлено. Проверьте смысл.", "msg-hint")],
      choices: [{ label: "Взять этот текст", value: "legal" }, { label: "Оставить как написал", value: "mine" },
        { label: "Дополнить рассказ", value: "more" }],
      placeholder: "Выберите вариант выше",
    });
    guard();
    if (choice === "legal") return result.text;
    if (choice === "more") return polish(state, field, await askField(field, "Дополняем рассказ", "", value), guard);
    return value;
  }

  // Что именно взято из документа или ответа: поле, значение и цитата, откуда оно.
  function tookList(state: DocState, keys: string[]) {
    return (
      <ul className="took-list">
        {keys.map((key) => {
          const field = state.schema.fields.find((f) => f.key === key);
          const value = state.answers[key] || "";
          const source = state.sources[key];
          return (
            <li key={key}>
              <span className="took-label">{(field?.question || key).replace(/\?$/, "")}</span>
              <span className="took-value">{field?.choices.length ? choiceLabel(value) : value.split("\n").join("; ")}</span>
              {source?.quote && source.quote !== value ? <span className="took-quote">«{source.quote}»</span> : null}
            </li>
          );
        })}
      </ul>
    );
  }

  // Без ИИ данные берутся только из судебного приказа (правила) — и только для возражений.
  const canAttach = (schema: Schema) => aiEnabled || Boolean(schema.accepts);

  async function useDocument(state: DocState, id: string) {
    const waiting = say(p(SEARCHING, "msg-hint"));
    let prefill: Prefill;
    try {
      prefill = await getJson<Prefill>(`/api/documents/${id}/prefill/${state.schema.slug}`);
    } catch (error) {
      warn((error as Error).message);
      return;
    } finally {
      remove(waiting);
    }
    const { added, conflicts, keys, differ } = absorbPrefill(state, prefill);
    const left = remaining(state);
    say(p(added
      ? `Из «${prefill.document}» взял ${plural(added, "значение", "значения", "значений")} для документа «${state.schema.short_title}»:`
      : `В «${prefill.document}» не нашёл данных для этого документа.`),
    added ? tookList(state, keys) : null,
    added ? p("Проверьте, особенно кто из людей в какой роли. В сводке эти значения отмечены «из документа».", "msg-hint") : null,
    conflicts ? p(`В ${plural(conflicts, "поле", "полях", "полях")} документы расходятся — оставил первое значение и отметил «проверьте»: ${
      differ.map((key) => (state.schema.fields.find((f) => f.key === key)?.question || key).replace(/\?$/, "")).join("; ")}.`, "msg-hint") : null,
    p(left ? (aiEnabled ? "Остальное уточню вопросами." : `Осталось ${plural(left, "вопрос", "вопроса", "вопросов")}.`)
      : "Все данные есть — сразу покажу сводку.", "msg-hint"));
  }

  const usableFor = (schema: Schema) => (doc: UploadedDoc) => aiEnabled || doc.kind === schema.accepts;
  const docsCode = (docs: UploadedDoc[]) => (docs.length === 1 ? `doc:${docs[0].id}` : `docs:${docs.map((doc) => doc.id).join(",")}`);
  const folderLabel = (folder: Folder, docs: UploadedDoc[]) => `Папка «${folder.name}»: ${plural(docs.length, "документ", "документа", "документов")}`;

  function attachChip(schema: Schema, label = "Прикрепить документ") {
    return (
      <AttachChip label={label} run={async () => {
        const source = await chooseSource("Откуда взять данные?", true, usableFor(schema));
        if (!source) return;
        if (source.type === "doc") return answer(`doc:${source.doc.id}`, `Документ: ${docName(source.doc)}`);
        if (source.type === "folder") return answer(docsCode(source.docs), folderLabel(source.folder, source.docs));
        const waiting = say(p(READING, "msg-hint"));
        try {
          const doc = await uploadFile(source.file);
          remove(waiting);
          if (!aiEnabled && doc.kind !== schema.accepts) {
            warn(`Не похоже на ${schema.accepts_title}: не нашёл в файле нужных данных. Загрузите другой файл или заполните по вопросам.`);
            return;
          }
          answer(`doc:${doc.id}`, `Документ: ${source.file.name}`);
        } catch (error) {
          remove(waiting);
          warn((error as Error).message);
        }
      }} />
    );
  }

  // Скрепка во время составления документа: файл дополняет ответы, а не открывает разбор документа.
  async function attachToTemplate() {
    const state = docFlow;
    if (!state) return;
    const source = await chooseSource("Откуда взять данные?", true, usableFor(state.schema));
    if (!source || docFlow !== state) return;
    let docs: UploadedDoc[];
    if (source.type === "file") {
      said(`Документ: ${source.file.name}`);
      const waiting = say(p(READING, "msg-hint"));
      try {
        docs = [await uploadFile(source.file)];
      } catch (error) {
        warn((error as Error).message);
        return;
      } finally {
        remove(waiting);
      }
      if (docFlow !== state) return;
      if (!canAttach(state.schema) || (!aiEnabled && docs[0].kind !== state.schema.accepts)) {
        warn("Документ сохранён в «Моих документах», но данные из него для этого шаблона сейчас взять не получится — ответьте на вопрос.");
        return;
      }
    } else {
      docs = source.type === "doc" ? [source.doc] : source.docs;
      said(source.type === "doc" ? `Документ: ${docName(source.doc)}` : folderLabel(source.folder, source.docs));
    }
    if (pending && pending.attachable) { answerQuietly(docsCode(docs)); return; }
    for (const doc of docs) {
      await useDocument(state, doc.id);
      if (docFlow !== state) return;
    }
    const field = pending?.field;
    if (field && state.answers[field.key]) answerQuietly(state.answers[field.key]);   // ответ на текущий вопрос нашёлся в файле
  }

  async function introChoice(state: DocState, guard: () => void) {
    const { schema } = state;
    // Как у юриста на приёме: что это за документ, на какой норме он основан, что понадобится, как он выглядит.
    const preview = (
      <button type="button" className="doc-preview" title="Открыть образец целиком" onClick={() => openPreview(schema.title, schema.preview)}>
        <span className="doc-thumb"><DocPage paragraphs={schema.preview} titles /></span>
      </button>
    );
    const count = remaining(state);
    const choices: Choice[] = [{ label: `Заполнить по вопросам (${plural(count, "вопрос", "вопроса", "вопросов")})`, value: "manual" }];
    const extra: ReactNode[] = [];
    if (canAttach(schema)) {
      await whenUploadedReady();
      guard();
      const seen = new Set<string>();
      const docs = uploadedStore.get().filter((d) => aiEnabled || d.kind === schema.accepts)
        .filter((d) => { const key = `${d.filename}|${d.size}`; return !seen.has(key) && Boolean(seen.add(key)); });
      for (const doc of docs.slice(0, 3)) {
        choices.push({ label: `Взять данные из: ${docName(doc)}`, value: `doc:${doc.id}` });
      }
      // Папка целиком: паспорт, свидетельство, решение суда одного дела — данные дополняют друг друга.
      for (const folder of foldersStore.get()) {
        const inside = docs.filter((doc) => doc.folder_id === folder.id);
        if (inside.length > 1 && choices.length < 6) choices.push({ label: `Взять данные из папки «${folder.name}» (${inside.length})`, value: docsCode(inside) });
      }
      extra.push(attachChip(schema));
    } else {
      extra.push(<button type="button" className="chip" disabled title="Распознавание документов подключается">Прикрепить документ · скоро</button>);
    }
    return ask({
      body: [p(schema.intro || `Составим документ «${schema.title}». ${schema.description}`),
        schema.needs ? p(schema.needs) : null, preview, schema.attach ? p(schema.attach, "msg-hint") : null],
      choices, extra, attachable: canAttach(schema), placeholder: "Выберите вариант выше",
    });
  }

  // Уточняющие вопросы помощника (pravosvod/interview.py): недостающее спрашивает своими словами, связанные поля —
  // одним вопросом, а ответ раскладывает по полям (каждое значение — с цитатой из ответа). Порядок шаблона не обязателен.
  async function interviewDoc(state: DocState, guard: () => void) {
    const { schema } = state;
    const fieldOf = (key: string) => schema.fields.find((f) => f.key === key);
    const history: { question: string; answer: string }[] = [];
    // Отвеченные вопросы: что спросили, что ответил человек и какие поля записаны из ответа.
    // «Назад» убирает эти поля и задаёт тот же вопрос снова — прежний ответ стоит в поле ввода, его можно дополнить.
    type Answered = { question: string; asked: Field[]; reply: string; set: string[] };
    const answered: Answered[] = [];
    let again: Answered | null = null;
    let message = "";
    let keys: string[] = [];
    let idle = 0;
    for (let turn = 0; turn < 40 && remaining(state); turn += 1) {
      let question: string;
      let asked: Field[];
      let draft = "";
      if (again) {                                // вернулись к прежнему вопросу — модель не спрашиваем
        question = again.question;
        asked = again.asked;
        draft = asked.length === 1 && asked[0].choices.length ? "" : again.reply;
        again = null;
      } else {
        const waiting = say(p("Думаю…", "msg-hint"));
        let step: InterviewStep;
        try {
          // Текущий ответ — только в message: в истории модель приняла бы его за уже разобранный.
          const past = message ? history.slice(0, -1) : history;
          step = await postJson<InterviewStep>(`/api/templates/${schema.slug}/interview`,
            { answers: state.answers, message, keys, history: past.slice(-6) });
        } catch {
          return;                                 // ИИ не ответил — дальше вопросы шаблона по порядку
        } finally {
          remove(waiting);
        }
        guard();
        const took: string[] = [];
        const set: string[] = [];
        for (const [key, found] of Object.entries(step.values || {})) {
          const field = fieldOf(key);
          if (!field || key in state.answers || checkField(field, found.value)) continue;   // формат не тот — спросим ещё
          state.answers[key] = found.value;
          set.push(key);
          if (!found.direct) {
            state.sources[key] = { quote: found.quote, document: MESSAGE_SOURCE };
            took.push(key);
          }
        }
        if (message && answered.length) answered[answered.length - 1].set.push(...set);   // взято из прошлого ответа
        if (took.length) say(p("Записал из вашего ответа:"), tookList(state, took));
        for (const [key, found] of Object.entries(step.values || {})) {
          const field = fieldOf(key);
          if (field?.legal && found.direct && state.answers[key] === found.value) state.answers[key] = await polish(state, field, found.value, guard);
        }
        asked = (step.keys || []).map(fieldOf).filter((f): f is Field => Boolean(f) && !(f!.key in state.answers));
        if (!remaining(state) || !asked.length || !step.question) return;
        // Ответ не дал ничего нового на тот же вопрос дважды — продолжим вопросами шаблона.
        idle = message && !took.length && asked.map((f) => f.key).join() === keys.join() ? idle + 1 : 0;
        if (idle >= 2) return;
        question = step.question;
      }
      const single = asked.length === 1 ? asked[0] : null;
      const left = remaining(state);
      const asking = ask({
        text: question,
        progress: `Осталось уточнить: ${plural(left, "пункт", "пункта", "пунктов")}`,
        hint: single ? (MULTILINE.has(single.kind) ? "Каждый пункт — с новой строки (Enter). Отправить — кнопкой со стрелкой или Ctrl+Enter." : "")
          : "Можно ответить одним сообщением, своими словами.",
        choices: single ? single.choices.map((c) => ({ label: choiceLabel(c), value: c })) : [],
        skip: asked.every((f) => f.optional) ? "Пропустить" : null,
        multiline: Boolean(single && MULTILINE.has(single.kind)),
        field: single,
        back: answered.length > 0,
        placeholder: single?.kind === "date" ? "ДД.ММ.ГГГГ" : single?.kind === "money" ? "Сумма цифрами" : "Ваш ответ",
      });
      if (draft) { input().value = draft; autoGrow(); }
      const reply = (await asking).trim();
      guard();
      message = "";
      keys = [];
      if (reply === BACK) {                       // к предыдущему вопросу: убрать записанное из того ответа
        const last = answered.pop()!;
        for (const key of last.set) { delete state.answers[key]; delete state.sources[key]; }
        history.pop();
        again = last;
        continue;
      }
      const entry: Answered = { question, asked, reply, set: [] };
      answered.push(entry);
      history.push({ question, answer: reply });
      if (!reply) {                               // «Пропустить» — необязательные поля оставляем пустыми
        for (const field of asked) state.answers[field.key] = "";
        entry.set = asked.map((f) => f.key);
        continue;
      }
      // Вариант из кнопок, дата или сумма в нужном формате — записываем сразу, без ИИ.
      if (single && (single.choices.length || single.kind === "date" || single.kind === "money") && !checkField(single, reply)) {
        state.answers[single.key] = reply;
        entry.set = [single.key];
        continue;
      }
      message = reply;
      keys = asked.map((f) => f.key);
    }
  }

  // initial — данные, которые помощник взял из сообщения человека («взыскать с Курбанова 300 тысяч»).
  function startDoc(slug: string, fromDoc: string | null = null, initial: Prefill | null = null) {
    const info = templates.find((item) => item.slug === slug);
    workspace.show(`doc:${slug}`, info?.short_title || "Составить документ", "doc", fromDoc);
    runFlow("Документ", async (guard) => {
      const schema = await getJson<Schema>(`/api/templates/${slug}`);
      guard();
      flowBar({ title: schema.short_title });
      const state: DocState = { schema, answers: {}, sources: {} };
      docFlow = state;
      try {
        if (initial && Object.keys(initial.answers || {}).length) {
          const { added, keys } = absorbPrefill(state, initial);
          say(p(`Из вашего сообщения взял ${plural(added, "значение", "значения", "значений")}:`), tookList(state, keys),
            p(`В сводке ${added === 1 ? "оно отмечено «из сообщения» — проверьте его" : "они отмечены «из сообщения» — проверьте их"}.`, "msg-hint"));
        }
        let next = fromDoc ? `doc:${fromDoc}` : await introChoice(state, guard);
        // Документы прикладывают по одному: паспорт, свидетельство, решение суда — данные дополняют друг друга.
        while (next.startsWith("doc:") || next.startsWith("docs:")) {
          for (const id of next.slice(next.indexOf(":") + 1).split(",")) {
            await useDocument(state, id);
            guard();
          }
          if (!remaining(state) || !canAttach(schema)) break;
          next = await ask({
            text: "Приложить ещё документ или продолжим по вопросам?",
            choices: [{ label: "Продолжить по вопросам", value: "manual" }],
            extra: [attachChip(schema, "Приложить ещё документ")], attachable: true, placeholder: "Выберите вариант выше",
          });
        }
        if (aiEnabled && remaining(state)) await interviewDoc(state, guard);
        // Без ИИ (или если он запутался) — вопросы шаблона по порядку; после уточняющих вопросов — только оставшиеся.
        // «Назад» — к предыдущему вопросу: прежний ответ стоит в поле ввода (у флажков — отмечен), его можно дополнить.
        const order: string[] = [];
        let draft = "";
        let current = "";
        for (;;) {
          const field = schema.fields.find((f) => !(f.key in state.answers) && relevant(f, state.answers));
          if (!field) break;
          // Сколько осталось — считаем заново: пропуск основного поля убирает уточняющие вопросы, а файл — найденные ответы.
          const left = remaining(state);
          const value = await askField(field, `Вопрос ${order.length + 1} из ${order.length + left}`, current, draft, order.length > 0);
          draft = current = "";
          if (value === BACK) {
            const previous = schema.fields.find((f) => f.key === order.pop())!;
            const before = state.answers[previous.key] || "";
            if (previous.kind === "checklist") current = before;
            else if (!previous.choices.length) draft = before;
            delete state.answers[previous.key];
            delete state.sources[previous.key];
            continue;
          }
          if (!(field.key in state.answers)) state.answers[field.key] = await polish(state, field, value, guard);
          order.push(field.key);
        }
        await showSummary(schema, state.answers, guard, state.sources);
      } finally {
        if (docFlow === state) docFlow = null;
        // Человек написал, пока готовилась сводка, — вопроса уже не будет: это новое сообщение помощнику.
        if (queued !== null && !docFlow) {
          const text = queued;
          queued = null;
          route(text);
        }
      }
    });
  }

  function editField(schema: Schema, answers: Record<string, string>, field: Field, sources: Record<string, Source>) {
    runFlow(schema.short_title, async (guard) => {
      const value = await askField(field, "Исправляем ответ", answers[field.key] || "");
      answers[field.key] = await polish({ schema, answers, sources }, field, value, guard);
      delete sources[field.key];                 // теперь это ваш ответ, а не данные из документа
      for (const next of schema.fields) {        // ответ открыл уточняющие вопросы (появился второй ответчик) — задаём их
        if (!(next.key in answers) && relevant(next, answers)) answers[next.key] = await askField(next, "Уточняем");
      }
      await showSummary(schema, answers, guard, sources);
    });
  }

  // Госпошлина в сводке: сумма с расчётом и нормой (калькулятор на сервере) или сверенная льгота.
  function dutyCard(schema: Schema, info: DutyInfo) {
    if (info.exempt) {
      return (
        <div className="duty-card">
          {p("Госпошлина", "result-label")}
          {p(info.exempt, "duty-exempt")}
          {info.checked ? p(`Норма сверена ${info.checked}.`, "msg-hint") : null}
        </div>
      );
    }
    const kind = info.title ? info.title[0].toUpperCase() + info.title.slice(1) : "Госпошлина";
    const calc: Calc = { title: schema.short_title, what: [kind, "суд общей юрисдикции", info.price ? `цена иска ${info.price}` : ""].filter(Boolean).join(", "),
      to_pay: info.to_pay!, amount: info.amount!, rounded: Boolean(info.rounded), formula: info.formula!, norm: info.norm!, edition: info.edition! };
    return (
      <div className="duty-card">
        <div className="duty-head">{p("Госпошлина к оплате", "result-label")}<strong className="duty-amount">{nb(info.to_pay!)}</strong></div>
        <dl className="facts">
          {info.price ? <><dt>Цена иска</dt><dd>{nb(info.price)}</dd></> : null}
          <dt>Расчёт</dt><dd>{nb(info.formula!)}</dd>
          <dt>Норма</dt><dd>{`${info.norm} (${info.edition})`}</dd>
        </dl>
        {info.rounded ? p(nb(`Точно по формуле ${info.amount}; к оплате округлено вверх до рубля.`), "msg-hint") : null}
        {info.note ? p(info.note, "msg-hint") : null}
        {p("Льготы по ст. 333.36 и 333.37 НК РФ калькулятор пока не учитывает.", "msg-hint")}
        <div className="chips"><SaveCalcButton calc={calc} /></div>
      </div>
    );
  }

  async function showSummary(schema: Schema, answers: Record<string, string>, guard: () => void, sources: Record<string, Source> = {}) {
    const check = await postJson<{ errors: string[]; warnings: string[]; duty?: DutyInfo | null }>(`/api/templates/${schema.slug}/check`, { answers });
    guard();
    const rows = schema.fields.filter((field) => relevant(field, answers)).map((field) => {
      const value = answers[field.key];
      const list = field.kind === "lines" || field.kind === "checklist";
      const shown = value ? (list ? value.split("\n").filter(Boolean).join("; ") : choiceLabel(value))
        : field.kind === "checklist" ? "без приложений"
          : !field.default ? "не указано" : field.default.includes("{today}") ? "сегодня" : "текст из образца";
      const source = sources[field.key];
      const where = source && source.document === MESSAGE_SOURCE ? "из сообщения" : "из документа";
      const tag = source ? (
        <span className="from-doc" title={`«${source.quote}»` + (source.document ? ` — ${source.document}` : "") + (source.note ? `\n${source.note}` : "")}>
          {source.note ? `${where} · проверьте` : where}
        </span>
      ) : null;
      return (
        <div key={field.key} className="summary-row">
          <dt>{field.question}</dt>
          <dd>
            <span className={value ? undefined : "empty"}>{shown}{tag}</span>
            <button type="button" className="link-button" onClick={() => editField(schema, answers, field, sources)}>Изменить</button>
          </dd>
        </div>
      );
    });
    const busy = new Store(false);
    const blocked = check.errors.length > 0;
    async function download() {
      busy.set(true);
      try {
        await downloadDocument(schema.slug, answers);
        const who = answers[schema.who] || "";
        await addSaved("doc", schema.short_title, who, { slug: schema.slug, answers: { ...answers }, who }).catch(() => null);
        say(p("Готово, файл скачан. Он сохранён в «Моих документах» — оттуда его можно скачать ещё раз. Перед подачей проверьте документ."), menu());
        promptLogin(boot.isGuest, "Документ сохранён в «Моих документах». Войдите, чтобы он хранился в аккаунте и открывался с любого устройства.");
      } catch (error) {
        warn((error as Error).message);
      } finally {
        busy.set(false);
      }
    }
    // Кнопка есть и над списком, и под ним: сводка длинная, листать до конца не нужно.
    say(<div className="summary-head">{p("Проверьте ответы", "msg-question")}<DownloadButton busy={busy} disabled={blocked} onClick={download} /></div>,
      check.duty ? dutyCard(schema, check.duty) : null,
      check.warnings.map((w, i) => <Fragment key={i}>{p(w, "note")}</Fragment>),
      check.errors.length ? p(check.errors.join(" "), "msg-error") : null,
      <dl className="summary">{rows}</dl>,
      <div className="chips"><DownloadButton busy={busy} disabled={blocked} onClick={download} /></div>);
  }

  /* ---- помощник: свободный текст понимает модель Yandex AI Studio (pravosvod/assistant.py), а без ИИ — ключевые слова */

  function showAssistant(reply: AssistantReply) {
    // Краткий вывод («Да…», «Нет…») — первой строкой; его числа и статьи сервер сверил с цитатами ниже.
    const parts: ReactNode[] = reply.answer ? [p(reply.answer, "assistant-answer")] : [];
    parts.push(...(reply.text || "").split(/\n+/).filter(Boolean).map((line) => p(line)));
    if (reply.points && reply.points.length) {
      // Утверждение, дословный фрагмент сверенного текста (в нём и номер статьи) и откуда он. Пункты по разъяснениям
      // Пленума ВС, решениям Конституционного Суда и судов Дагестана сервер ставит после пунктов по закону.
      parts.push(<ul className="assistant-points">{reply.points.map((point, i) => (
        <li key={i} className={isDecision(point.source_id) || isExplanation(point.source_id) || isCourtDecision(point.source_id) ? "assistant-practice" : undefined}>
          {point.text ? <span>{point.text}</span> : null}
          <span className="assistant-quote">«{point.quote}»</span>
          <span className="assistant-source">{`Источник: ${point.source}${point.checked ? `, ${isDecision(point.source_id) ? "текст сверен" : isExplanation(point.source_id) ? plenumChecked(point.checked) : isCourtDecision(point.source_id) ? "текст скачан" : "нормы сверены"} ${point.checked}` : ""}`}</span>
          {articleId(point.source_id, point.quote) ? <ArticleText id={articleId(point.source_id, point.quote)} label={point.source} /> : null}
          {isDecision(point.source_id) ? <DecisionText id={point.source_id!} label={point.source} /> : null}
          {isExplanation(point.source_id) ? <PlenumText id={point.source_id!} /> : null}
          {isCourtDecision(point.source_id) ? <CourtDecisionText id={point.source_id!} label={point.source} /> : null}
        </li>
      ))}</ul>);
    }
    // Ответа с цитатой нет, но статьи по вопросу нашлись — их текст можно открыть и прочитать самому.
    if (reply.articles && reply.articles.length) {
      parts.push(<ul className="assistant-points assistant-maybe">{reply.articles.map((article) => (
        <li key={article.source_id}>
          <span className="assistant-maybe-label">{article.label}</span>
          <ArticleText id={article.source_id} label={article.label} />
        </li>
      ))}</ul>);
    }
    // Вопрос по праву — предлагаем документ, о котором шла речь; про сервис — меню.
    const offers = new Map<string, string>();
    for (const point of reply.points || []) {
      const info = point.template && templates.find((t) => t.slug === point.template);
      if (info) offers.set(info.slug, info.short_title);
    }
    if (reply.suggest) offers.set(reply.suggest.slug, reply.suggest.title);
    const chips = [...offers].slice(0, 3).map(([slug, title]) => <Fragment key={slug}>{chip(`Составить: ${title}`, () => { said(title); startDoc(slug); })}</Fragment>);
    // Похожие вопросы — нажатие задаёт вопрос помощнику, как будто его написал человек.
    const related = (reply.suggestions || []).slice(0, 3);
    if (related.length) {
      parts.push(p("Возможно, вы имели в виду:", "msg-hint"),
        <div className="chips assistant-related">{related.map((question) => (
          <Fragment key={question}>{chip(question, () => { said(question); askAssistant(question); })}</Fragment>
        ))}</div>);
    }
    say(...parts, chips.length ? <div className="chips">{chips}</div> : reply.action === "clarify" ? null : menu());
  }

  async function askAssistant(text: string) {
    const id = ++flow;
    cancelPending();
    flowBar({ hidden: true });
    const waiting = say(p("Думаю…", "msg-hint"));
    const timer = window.setTimeout(() => { if (id === flow) reword(waiting, p(SLOW_ANSWER, "msg-hint")); }, SLOW_AFTER_MS);
    const stopWaiting = () => { window.clearTimeout(timer); remove(waiting); };
    let reply: AssistantReply;
    try {
      reply = await postJson<AssistantReply>("/api/assistant", { message: text, history: assistantHistory.slice(-8) });
    } catch (error) {
      stopWaiting();
      if (id !== flow) return;
      if (error instanceof ApiError && error.status === 429) { say(p(error.message), menu()); return; }
      keywordRoute(text);                        // ИИ недоступен — работаем по ключевым словам
      return;
    }
    stopWaiting();
    if (id !== flow) return;                     // пока ждали ответа, человек начал другое
    assistantHistory.push({ role: "user", text }, { role: "assistant", text: reply.text || (reply as { title?: string }).title || reply.action });
    if (reply.action === "document" && reply.template) {
      startDoc(reply.template, null, reply.prefill || null);       // вступление к шаблону само объяснит, что это за документ
    } else if (reply.action === "duty") {
      if (reply.duty) {
        showCalc(reply.duty);
        addSaved("calc", reply.duty.title, reply.duty.to_pay, reply.duty).catch(() => { /* расчёт уже показан */ });
      } else {
        startDuty();
      }
    } else if (reply.action === "review") {
      reviewWelcome();
    } else {
      showAssistant(reply);
    }
  }

  function route(text: string) {
    if (aiEnabled && looksLikeDocument(text)) return offerReview(text);
    if (aiEnabled) return askAssistant(text);
    return keywordRoute(text);
  }

  // В чат вставили текст документа — предлагаем разбор, а не ответ помощника на него как на вопрос.
  function offerReview(text: string) {
    say(p("Похоже, это текст документа. Разобрать его? Подготовлю выжимку по виду документа, список норм, на которые он ссылается, и слабые места."),
      <div className="chips">
        {chip("Разобрать документ", () => reviewText(text))}
        {chip("Нет, это вопрос", () => askAssistant(text))}
      </div>);
  }

  function keywordRoute(text: string) {
    const t = text.toLowerCase();
    if (/разобра|анализ.*документ|провер.*договор/.test(t)) return reviewWelcome();
    if (/пошлин/.test(t) && !/отсрочк|рассрочк/.test(t)) return startDuty();
    // Слова для каждого шаблона заданы в его файле вопросов (поле match).
    const found = templates.find((tpl) => tpl.match && new RegExp(tpl.match, "i").test(t));
    if (found) return startDoc(found.slug);
    if (/практик|решени/.test(t)) return say(p("Поиск судебной практики по судам Дагестана и Верховному Суду — следующий большой раздел, он в работе. Пока могу помочь с документами и госпошлиной."), menu());
    say(p(aiEnabled
      ? "Не получилось связаться с ИИ — попробуйте написать ещё раз через минуту. А пока можно выбрать действие:"
      : "Я составляю документы по образцам адвоката, считаю госпошлину и разбираю загруженные документы. Напишите, что нужно, — например: «возражения на судебный приказ» или «пошлина с иска на 300 000 ₽»."), menu());
  }

  function start(code: string, fromDoc: string | null = null) {
    if (code === "law") return aiEnabled ? lawWelcome() : keywordRoute("");
    if (code === "review") return reviewWelcome();
    if (code.startsWith("review:")) return openReview(code.slice(7));
    if (code === "duty") return startDuty();
    if (code.startsWith("doc:")) return startDoc(code.slice(4), fromDoc);
    if (code.startsWith("calc:")) {
      whenSavedReady().then(() => {
        const item = savedStore.get().calc.find((c) => c.id === code.slice(5));
        if (item) showCalc(item.payload as Calc, item.id);
      });
    }
  }

  /* ---- поле ввода */

  function autoGrow() {
    const node = input();
    node.style.height = "auto";
    node.style.height = `${Math.min(node.scrollHeight, 200)}px`;
    node.style.overflowY = node.scrollHeight > 200 ? "auto" : "hidden";
  }

  function submit() {
    const node = input();
    const text = node.value.trim();
    if (!text || reviewBusy) return;
    if (reviewDoc) {
      if (text.length > 3000) { warn("Сократите вопрос до 3000 символов."); return; }
      node.value = ""; autoGrow();
      requestReview(text); return;
    }
    if (reviewIntro) {
      if (text.length < 80) { warn("Прикрепите документ кнопкой выше или вставьте его текст целиком."); return; }
      node.value = ""; autoGrow();
      reviewText(text); return;
    }
    node.value = "";
    autoGrow();
    if (pending && /^(отмена|отменить|стоп|прервать)$/i.test(text)) { said(text); cancelFlow(); return; }
    if (pending && pending.skip && /^пропустить$/i.test(text)) { answer("", pending.skip); return; }
    if (pending && pending.back && /^назад$/i.test(text)) { answer(BACK, "Назад"); return; }
    if (pending && pending.choices.length) {
      const lower = text.toLowerCase();
      const match = pending.choices.find((c) => c.label.toLowerCase() === lower || c.value === lower)
        || pending.choices.find((c) => c.label.toLowerCase().startsWith(lower));
      if (match) answer(match.value, match.label);
      else { said(text); warn("Выберите один из вариантов кнопкой выше."); }
      return;
    }
    if (pending) { answer(text, text); return; }
    // Мастер документа ещё готовит вопрос (ИИ думает) — ответ не теряем: засчитаем его, как только вопрос появится.
    if (docFlow) { said(text); queued = text; return; }
    said(text);
    route(text);
  }

  // Enter — отправить; в ответе из нескольких строк Enter — новая строка, Ctrl+Enter — отправить.
  function keyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      if (pending && pending.multiline && !(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      submit();
    }
  }

  const attach = () => (docFlow ? attachToTemplate() : attachForReview());

  // Карточки на главной запускают сценарий прямо здесь, без перезагрузки.
  // label — реплика человека в чате; без неё сценарий начинается сразу с ответа помощника («Загрузить документ»).
  function startFromLink(code: string, label: string) {
    document.body.classList.remove("nav-open");
    if (label) said(label);
    start(code);
  }

  /* ---- сохранение чата: переписка уходит в аккаунт и появляется в боковой панели (pravosvod/chats.py) */

  const saver = { id: currentChatStore.get(), last: "", timer: undefined as ReturnType<typeof setTimeout> | undefined, queue: Promise.resolve() };

  // Переписка текстом: реплики человека и ответы помощника без кнопок, образцов и счётчика вопросов.
  function transcript() {
    const items: { role: "user" | "assistant"; text: string }[] = [];
    for (const node of env.messages()?.children || []) {
      const bubble = node.querySelector(".bubble") as HTMLElement | null;
      if (!bubble) continue;
      const role = node.classList.contains("msg-user") ? "user" : "assistant";
      const text = role === "user" ? (bubble.textContent || "").trim() : [...bubble.children]
        .filter((child) => !child.matches(".bot-name, .chips, .doc-preview, .msg-progress, button"))
        .map((child) => (child as HTMLElement).innerText.trim()).filter(Boolean).join("\n");
      if (text && !TRANSIENT.has(text)) items.push({ role, text });
    }
    return items;
  }

  // Экран очищается (разбор документа): дальше пишем в новый чат, сохранённый не перезаписываем.
  function detachChat() {
    clearTimeout(saver.timer);
    saver.id = null;
    currentChatStore.set(null);
    saver.last = "";
  }

  function saveChat() {
    saver.timer = undefined;
    if (!alive) return;
    if (reviewDoc || reviewIntro) return;            // разбор документа хранится вместе с документом
    const items = transcript();
    const first = items.find((item) => item.role === "user");
    if (!first) return;                              // чат появляется с первой репликой человека
    const snapshot = JSON.stringify(items);
    if (snapshot === saver.last) return;
    saver.queue = saver.queue.then(async () => {
      try {
        if (!saver.id) {
          const created = await postJson<ChatBrief>("/api/chats", { title: first.text, messages: items });
          saver.id = created.id;
          rememberChat({ ...created, messages: items });
          upsertChat(created);
          if (alive) {                                        // из чата уже ушли — адрес чужого раздела не трогаем
            currentChatStore.set(created.id);
            const url = new URL(location.href);
            url.searchParams.set("chat", created.id);
            history.replaceState(history.state, "", url);    // после перезагрузки откроется этот же чат
            workspace.rename(created.title);
          }
        } else {
          const updated = await postJson<ChatBrief>(`/api/chats/${saver.id}`, { messages: items }, "PUT");
          rememberChat({ ...updated, messages: items });
          upsertChat(updated);
        }
        saver.last = snapshot;
      } catch { /* не сохранилось — попробуем со следующим сообщением */ }
    });
  }

  function watchMessages(container: HTMLElement) {
    const observer = new MutationObserver(() => {
      clearTimeout(saver.timer);
      saver.timer = setTimeout(saveChat, 800);
    });
    observer.observe(container, { childList: true, subtree: true });
    const onHide = () => { if (saver.timer) saveChat(); };
    window.addEventListener("pagehide", onHide);
    return () => { observer.disconnect(); window.removeEventListener("pagehide", onHide); };
  }

  // Открыть сохранённый чат: показать переписку и продолжить разговор с того же места.
  async function openChat(id: string) {
    // Переписка обычно уже загружена заранее (prefetchChats) — тогда чат появляется сразу, без ожидания сервера.
    let chatData = cachedChat(id);
    if (!chatData) try {
      chatData = await loadChat(id);
    } catch {
      if (!alive) return;
      saver.id = null;
      currentChatStore.set(null);
      history.replaceState(history.state, "", "/");
      say(p("Этот чат не найден — возможно, он удалён. Начните новый."), menu());
      return;
    }
    if (!alive) return;
    for (const item of chatData.messages) {
      if (item.role === "user") said(item.text);
      else say(...item.text.split("\n").map((line) => p(line)));
    }
    say(p(RESUME, "msg-hint"));
    history.replaceState(history.state, "", `/?chat=${id}`);   // незаконченный шаг документа заново не открываем
    workspace.rename(chatData.title);
    // Снимок — после того как React нарисует переписку, иначе первое же сохранение посчитает её новой.
    requestAnimationFrame(() => { saver.last = JSON.stringify(transcript()); });
    assistantHistory.push(...chatData.messages.slice(-8).map(({ role, text }) => ({ role, text: text.slice(0, 2000) })));
    document.title = `${chatData.title} — Правосвод`;
  }

  const openCalcHere = (item: SavedItem) => showCalc(item.payload as Calc, item.id);

  // Чат переименовали в боковой панели — новое название сразу в верхней строке и во вкладке браузера.
  let shownTitle = "";
  const stopTitleWatch = chatsStore.subscribe(() => {
    const chat = saver.id ? chatsStore.get().find((other) => other.id === saver.id) : undefined;
    if (!alive || !chat || chat.title === shownTitle) return;
    const first = !shownTitle;                       // чат только что сохранён или открыт — это не переименование
    shownTitle = chat.title;
    if (first) return;
    workspace.rename(chat.title);
    document.title = `${chat.title} — Правосвод`;
  });

  function init() {
    calcOpener.open = openCalcHere;
    const params = new URLSearchParams(location.search);
    const code = params.get("start");
    const chatId = currentChatStore.get();
    if (chatId) openChat(chatId);
    else if (code) start(code, params.get("from"));
  }

  // Переход в другой раздел: несохранённую переписку отправляем сразу, сценарии и разбор останавливаем.
  function dispose() {
    if (!alive) return;
    if (saver.timer) {
      clearTimeout(saver.timer);
      saveChat();
    }
    alive = false;
    stopTitleWatch();
    flow += 1;
    reviewEpoch += 1;
    clearTimeout(reviewTimer);
    if (pending) {
      const current = pending;
      pending = null;
      current.reject(new Cancelled());
    }
    workspace.dispose();
    if (calcOpener.open === openCalcHere) calcOpener.open = null;
    resetChatState();
    document.body.classList.remove("chatting");
  }

  return { init, dispose, submit, keyDown, autoGrow, attach, cancelFlow, startFromLink, reviewWelcome, watchMessages };
}

